import { useState } from 'react';

interface RepairCase {
  id: number;
  title: string;
  problem: string;
  solution: string;
  duration: string;
  price: string;
  beforeImage: string;
  afterImage: string;
  material: string;
}

const repairCases: RepairCase[] = [
  {
    id: 1,
    title: 'Fenêtre PVC - Mécanisme Bloqué',
    problem: 'Poignée bloquée en position fermée, impossible d\'ouvrir la fenêtre',
    solution: 'Remplacement du mécanisme de crémone et lubrification complète',
    duration: '45 minutes',
    price: '120€',
    beforeImage: 'https://public.readdy.ai/ai/img_res/26616914a1051b828a628ba47e1dd2a8.jpg',
    afterImage: 'https://readdy.ai/api/search-image?query=repaired%20window%20handle%20mechanism%20working%20perfectly%20on%20white%20pvc%20window%20frame%20close%20up%20view%20showing%20new%20chrome%20hardware%20component%20professional%20quality%20simple%20clean%20background&width=600&height=400&seq=gallery1after&orientation=landscape',
    material: 'PVC'
  },
  {
    id: 2,
    title: 'Baie Vitrée Alu - Joint Défaillant',
    problem: 'Infiltration d\'eau et courants d\'air, joint d\'étanchéité usé',
    solution: 'Remplacement complet des joints périphériques et réglage',
    duration: '1h30',
    price: '180€',
    beforeImage: 'https://readdy.ai/api/search-image?query=damaged%20weatherstripping%20seal%20on%20aluminum%20sliding%20glass%20door%20showing%20water%20infiltration%20worn%20rubber%20gasket%20close%20up%20professional%20repair%20context%20simple%20clean%20background&width=600&height=400&seq=gallery2before&orientation=landscape',
    afterImage: 'https://readdy.ai/api/search-image?query=new%20weatherstripping%20seal%20installed%20on%20aluminum%20sliding%20glass%20door%20perfect%20black%20rubber%20gasket%20close%20up%20professional%20quality%20repair%20simple%20clean%20background&width=600&height=400&seq=gallery2after&orientation=landscape',
    material: 'Aluminium'
  },
  {
    id: 3,
    title: 'Fenêtre Bois - Vitrage Fissuré',
    problem: 'Double vitrage fissuré suite à un choc, perte d\'isolation',
    solution: 'Remplacement du vitrage isolant avec parclose assortie',
    duration: '2h',
    price: '250€',
    beforeImage: 'https://readdy.ai/api/search-image?query=cracked%20double%20glazing%20glass%20pane%20on%20wooden%20window%20frame%20showing%20spider%20web%20crack%20pattern%20damaged%20insulation%20professional%20repair%20context%20simple%20clean%20background&width=600&height=400&seq=gallery3before&orientation=landscape',
    afterImage: 'https://readdy.ai/api/search-image?query=new%20double%20glazing%20glass%20pane%20installed%20on%20wooden%20window%20frame%20perfectly%20clear%20transparent%20professional%20quality%20repair%20simple%20clean%20background&width=600&height=400&seq=gallery3after&orientation=landscape',
    material: 'Bois'
  },
  {
    id: 4,
    title: 'Fenêtre PVC - Charnières Affaissées',
    problem: 'Fenêtre qui frotte et ferme mal, charnières usées',
    solution: 'Remplacement des charnières et réglage complet de l\'ouvrant',
    duration: '1h',
    price: '150€',
    beforeImage: 'https://readdy.ai/api/search-image?query=sagging%20window%20hinges%20on%20white%20pvc%20window%20showing%20misalignment%20worn%20metal%20hardware%20component%20professional%20repair%20context%20simple%20clean%20background&width=600&height=400&seq=gallery4before&orientation=landscape',
    afterImage: 'https://readdy.ai/api/search-image?query=new%20window%20hinges%20installed%20on%20white%20pvc%20window%20perfectly%20aligned%20chrome%20metal%20hardware%20component%20professional%20quality%20repair%20simple%20clean%20background&width=600&height=400&seq=gallery4after&orientation=landscape',
    material: 'PVC'
  }
];

export default function Gallery() {
  const [selectedCase, setSelectedCase] = useState<RepairCase | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('Tous');

  const filters = ['Tous', 'PVC', 'Aluminium', 'Bois'];

  const filteredCases = activeFilter === 'Tous' 
    ? repairCases 
    : repairCases.filter(c => c.material === activeFilter);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Exemples de Réparations Réalisées
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Découvrez nos interventions avant/après avec détails techniques et tarifs
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                  activeFilter === filter
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredCases.map((repairCase) => (
            <div
              key={repairCase.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedCase(repairCase)}
            >
              <div className="grid grid-cols-2 gap-0">
                <div className="relative">
                  <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold z-10">
                    Avant
                  </div>
                  <div className="w-full h-64">
                    <img
                      src={repairCase.beforeImage}
                      alt={`Avant réparation - ${repairCase.title}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                      width="600"
                      height="400"
                    />
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold z-10">
                    Après
                  </div>
                  <div className="w-full h-64">
                    <img
                      src={repairCase.afterImage}
                      alt={`Après réparation - ${repairCase.title}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                      width="600"
                      height="400"
                    />
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900">{repairCase.title}</h3>
                  <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {repairCase.material}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <i className="ri-error-warning-line text-red-500 text-lg mt-0.5 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Problème :</p>
                      <p className="text-sm text-gray-600">{repairCase.problem}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <i className="ri-tools-line text-teal-600 text-lg mt-0.5 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Solution :</p>
                      <p className="text-sm text-gray-600">{repairCase.solution}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-gray-500"></i>
                    <span className="text-sm text-gray-600">{repairCase.duration}</span>
                  </div>
                  <div className="text-2xl font-bold text-teal-600">{repairCase.price}</div>
                </div>

                <button className="w-full mt-4 bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors whitespace-nowrap text-sm">
                  Voir les détails
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-euro-circle-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">3 à 5x Moins Cher</h3>
              <p className="text-gray-600">Qu'un remplacement complet de fenêtre</p>
            </div>

            <div>
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Intervention Rapide</h3>
              <p className="text-gray-600">La plupart des réparations en moins de 2h</p>
            </div>

            <div>
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Garantie 2 Ans</h3>
              <p className="text-gray-600">Sur toutes nos réparations</p>
            </div>
          </div>
        </div>
      </div>

      {selectedCase && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setSelectedCase(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">{selectedCase.title}</h2>
              <button
                onClick={() => setSelectedCase(null)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold inline-block mb-3">
                    Avant Réparation
                  </div>
                  <div className="w-full h-80 rounded-xl overflow-hidden">
                    <img
                      src={selectedCase.beforeImage}
                      alt="Avant réparation"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="400"
                    />
                  </div>
                </div>
                <div>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold inline-block mb-3">
                    Après Réparation
                  </div>
                  <div className="w-full h-80 rounded-xl overflow-hidden">
                    <img
                      src={selectedCase.afterImage}
                      alt="Après réparation"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="400"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-line text-red-500 text-2xl mt-0.5"></i>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Problème Constaté</h3>
                      <p className="text-gray-700">{selectedCase.problem}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-tools-line text-teal-600 text-2xl mt-0.5"></i>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Solution Appliquée</h3>
                      <p className="text-gray-700">{selectedCase.solution}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl text-center">
                    <i className="ri-time-line text-2xl text-gray-600 mb-2"></i>
                    <p className="text-sm text-gray-600 mb-1">Durée</p>
                    <p className="text-lg font-bold text-gray-900">{selectedCase.duration}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl text-center">
                    <i className="ri-money-euro-circle-line text-2xl text-gray-600 mb-2"></i>
                    <p className="text-sm text-gray-600 mb-1">Tarif</p>
                    <p className="text-lg font-bold text-teal-600">{selectedCase.price}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl text-center">
                    <i className="ri-shield-check-line text-2xl text-gray-600 mb-2"></i>
                    <p className="text-sm text-gray-600 mb-1">Garantie</p>
                    <p className="text-lg font-bold text-gray-900">2 ans</p>
                  </div>
                </div>

                <a
                  href="#contact"
                  className="block w-full bg-teal-600 text-white py-4 rounded-lg font-semibold text-center hover:bg-teal-700 transition-colors whitespace-nowrap"
                  onClick={() => setSelectedCase(null)}
                >
                  <i className="ri-tools-line mr-2"></i>
                  Demander une Réparation Similaire
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
